import CameraService, { CameraPermissionState } from '../../../helpers/classes/CameraService';
import EventService from '../../../helpers/classes/EventService';
import RenderingService from '../../../helpers/classes/RenderingService';
import TemplateService from '../../../helpers/classes/TemplateService';
import { JourneyContainer } from '../../classes/JourneyContainer';
import { FlowState } from '../../enums/FlowState';
import { ChangeMode, InputProvider } from '../../enums/InputProvider';
import { JourneyEvent } from '../../enums/JourneyEvent';
import { Templates } from '../../enums/Templates';
import { CameraProvider } from '../provider/camera';
import { SmartCaptureProvider } from '../provider/smart-capture';
import { CropperProvider } from '../provider/cropper';
import { FileSystemProvider } from '../provider/fileSystem';
import { ScannerProvider } from '../provider/scanner';
import LanguageService from '../../../helpers/classes/LanguageService';
import { TranslationKey } from '../../enums/Translation';
import { isFirefox, browserName, isSafari, isChrome, isIOS } from 'mobile-device-detect';

export class GatewayStep {
	public static render(this: JourneyContainer, isFromTripleScanGuidancePage: boolean = false, isPredefinedJourney: boolean = false, selectedInputProvider: InputProvider = InputProvider.NONE) {
		return TemplateService.renderToContainer(this._container, Templates.Gateway)
			.then(() => GatewayStep.getInputProvider.call(this, isPredefinedJourney, selectedInputProvider))
			.then((provider) => this.changeInputProvider(provider))
			.then((inputProvider): Promise<void> => {
				if (this._cropResponse !== undefined) {
					return CropperProvider.render.call(this);
				} else if (inputProvider === InputProvider.FILESYSTEM && this._cropResponse === undefined) {

					return FileSystemProvider.render.call(this);
				} else if (inputProvider === InputProvider.SMART_CAPTURE && this._cropResponse === undefined) {
					if (isIOS && (isSafari || isChrome)) {
						// ENTERPRISE-393
						return navigator.mediaDevices.getUserMedia({ video: true })
							.then((stream) => {
								// camera is available
								return SmartCaptureProvider.render.call(this, isFromTripleScanGuidancePage, isPredefinedJourney);
							},
								(e) => {
									// camera not available "or blocked by user"

									if (!isPredefinedJourney) {
										return this.changeInputProvider(InputProvider.FILESYSTEM, ChangeMode.FORCE)
											.then(() => GatewayStep.getInputProvider.call(this, isPredefinedJourney)
												.then(() => FileSystemProvider.render.call(this)));
									}
								});
					}

					else
						return Promise.all([
							CameraService.getCameraPermission(),
							CameraService.hasDeviceCapableOfResolution()
						])
							.then(([permission, resolution]) => {
								if ((permission == CameraPermissionState.GRANTED || permission == CameraPermissionState.UNKNOWN) && resolution) {

									if (this._config.defaultInputProvider?.toLowerCase() === InputProvider.CAMERA.toLowerCase())
										return CameraProvider.render.call(this, isFromTripleScanGuidancePage, isPredefinedJourney);
									else
										return SmartCaptureProvider.render.call(this, isFromTripleScanGuidancePage, isPredefinedJourney);
								} else {
									// ENTERPRISE-459
									if (!isPredefinedJourney) {
										return this.changeInputProvider(InputProvider.FILESYSTEM, ChangeMode.FORCE)
											.then(() => GatewayStep.getInputProvider.call(this, isPredefinedJourney)
												.then(() => FileSystemProvider.render.call(this)));
									}
									else {
										TemplateService.renderToContainer(this._container, Templates.HasNoCamera);
									}
								}
							});

				} else if (inputProvider === InputProvider.CAMERA && this._cropResponse === undefined) {
					//ENT-3248
					if (isIOS && (isSafari || isChrome)) {
						return navigator.mediaDevices.getUserMedia({ video: true })
							.then((stream) => {
								return CameraProvider.render.call(this, isFromTripleScanGuidancePage, isPredefinedJourney);
							},
								(e) => {
									if (!isPredefinedJourney) {
										return this.changeInputProvider(InputProvider.FILESYSTEM, ChangeMode.FORCE)
											.then(() => GatewayStep.getInputProvider.call(this, isPredefinedJourney)
												.then(() => FileSystemProvider.render.call(this)));
									}
									else {
										TemplateService.renderToContainer(this._container, Templates.HasNoCamera);
									}
								});
					}

					else
						return Promise.all([
							CameraService.getCameraPermission(),
						])
							.then(([permission]) => {
								if (permission == CameraPermissionState.GRANTED || permission == CameraPermissionState.UNKNOWN) {
									return CameraProvider.render.call(this, isFromTripleScanGuidancePage, isPredefinedJourney);
								} else {
									if (!isPredefinedJourney) {
										return this.changeInputProvider(InputProvider.FILESYSTEM, ChangeMode.FORCE)
											.then(() => GatewayStep.getInputProvider.call(this, isPredefinedJourney)
												.then(() => FileSystemProvider.render.call(this)));
									}
									else {
										TemplateService.renderToContainer(this._container, Templates.HasNoCamera);
									}
								}
							});
				} else if (inputProvider === InputProvider.SCANNER && this._cropResponse === undefined) {
					return ScannerProvider.render.call(this);
				} else {
					return Promise.resolve();
				}
			})
			.then(() => EventService.publish(JourneyEvent.DISPLAY_STEP, { step: Templates.Gateway }));

	}

	private static toggleFileUpload = false;

	private static getInputProvider(this: JourneyContainer, isPredefinedJourney: boolean = false, selectedInputProvider: InputProvider = InputProvider.NONE): Promise<InputProvider> {
		return TemplateService.getElements(this._container, {
			uploadToggleButton: '[data-jcs-element="gateway__upload__toggle"]',
			uploadLabel: '[data-jcs-element="gateway__upload__toggle__upload__label"]',
			cameraLabel: '[data-jcs-element="gateway__upload__toggle__camera__label"]',
			cameraStatus: '[data-jcs-element="gateway__camera__status"]',
		}).then(({ uploadToggleButton, uploadLabel, cameraLabel, cameraStatus }): InputProvider => {
			RenderingService.hideObject(uploadToggleButton);

			if (this.requiresCamera()) {

				Promise.all([
					CameraService.getCameraPermission(),
					CameraService.hasDeviceCapableOfResolution()
				])
					.then(([permission, resolution]) => {
						if (!resolution) {
							return RenderingService.setText(cameraStatus, LanguageService.getTranslation(TranslationKey.EXCEPTION_PLATFORM_CAMERA_NONE_SUITABLE));
						} else {
							switch (permission) {
								case CameraPermissionState.GRANTED:
									return RenderingService.hideObject(cameraStatus);

								case CameraPermissionState.DENIED:
								case CameraPermissionState.PROMPT:
									return RenderingService.setText(cameraStatus, LanguageService.getTranslation(TranslationKey.EXCEPTION_PLATFORM_CAMERA_PERMISSION_DENIED));

								default:
									return RenderingService.setText(cameraStatus, LanguageService.getTranslation(TranslationKey.EXCEPTION_PLATFORM_CAMERA_PERMISSION_UNAVAILABLE));
							}
						}
					});
			}

			if (this._ready === true && this._journey) {
				if (
					(this._journey.action === FlowState.SELFIE ||
						this._journey.action === FlowState.ADDRESS ||
						this._journey.action === FlowState.COVIDPASS) && !isPredefinedJourney
				) {
					RenderingService.showObject(uploadToggleButton);

					if (uploadToggleButton instanceof HTMLButtonElement &&
						uploadLabel instanceof HTMLElement &&
						cameraLabel instanceof HTMLElement) {
						if (this._inputProvider === undefined) {
							this._inputProvider = InputProvider.FILESYSTEM;
						} else if (this._inputProvider === InputProvider.SMART_CAPTURE || this._inputProvider === InputProvider.SCANNER) {
							this._inputProvider = InputProvider.CAMERA;
						}

						GatewayStep.toggleFileUpload = this._inputProvider !== InputProvider.FILESYSTEM && !isPredefinedJourney;

						GatewayStep.toggleUploadLabel.call(this, GatewayStep.toggleFileUpload, uploadLabel, uploadToggleButton, cameraLabel);

						uploadToggleButton.addEventListener('click', () => {
							GatewayStep.toggleFileUpload = !GatewayStep.toggleFileUpload;

							GatewayStep.toggleUploadLabel.call(this, GatewayStep.toggleFileUpload, uploadLabel, uploadToggleButton, cameraLabel);

							this._inputProvider = GatewayStep.toggleFileUpload ? this._providers[0] : InputProvider.FILESYSTEM;

							if (
								this._inputProvider !== InputProvider.CAMERA &&
								this._inputProvider !== InputProvider.SMART_CAPTURE
							) {
								CameraService.stopStreaming();
							}

							this.evaluate();
						});

						return GatewayStep.toggleFileUpload ? this._inputProvider : InputProvider.FILESYSTEM;
					} else {
						if (this._inputProvider === undefined || this._inputProvider === InputProvider.SCANNER) {
							return InputProvider.FILESYSTEM;
						} else if (this._inputProvider === InputProvider.SMART_CAPTURE) {
							return InputProvider.CAMERA;
						} else {
							return this._inputProvider;
						}
					}
				}


				else if (
					this._providers.indexOf(InputProvider.SCANNER) >= 0 &&
					(
						this._journey.action === FlowState.IDENTITY_FRONT ||
						this._journey.action === FlowState.IDENTITY_BACK
					)
				) {
					return InputProvider.SCANNER;
				} else if (
					!(this._providers.indexOf(InputProvider.CAMERA) >= 0 ||
						this._providers.indexOf(InputProvider.SMART_CAPTURE) >= 0) && !isPredefinedJourney
				) {
					return InputProvider.FILESYSTEM;
				} else if ((this._journey.action !== FlowState.NONE) && !isPredefinedJourney) {
					RenderingService.showObject(uploadToggleButton);

					if (uploadToggleButton instanceof HTMLButtonElement &&
						uploadLabel instanceof HTMLElement &&
						cameraLabel instanceof HTMLElement) {

						if (this._inputProvider === undefined || this._inputProvider === InputProvider.SCANNER) {
							this._inputProvider = InputProvider.FILESYSTEM;
						}

						GatewayStep.toggleFileUpload = this._inputProvider !== InputProvider.FILESYSTEM;

						GatewayStep.toggleUploadLabel.call(this, GatewayStep.toggleFileUpload, uploadLabel, uploadToggleButton, cameraLabel);

						uploadToggleButton.addEventListener('click', () => {
							GatewayStep.toggleFileUpload = !GatewayStep.toggleFileUpload;
							GatewayStep.toggleUploadLabel.call(this, GatewayStep.toggleFileUpload, uploadLabel, uploadToggleButton, cameraLabel);


							this._inputProvider = GatewayStep.toggleFileUpload ? this._providers[0] : InputProvider.FILESYSTEM;

							if (
								this._inputProvider !== InputProvider.CAMERA &&
								this._inputProvider !== InputProvider.SMART_CAPTURE
							) {
								CameraService.stopStreaming();
							}

							this.evaluate();
						});

						return GatewayStep.toggleFileUpload ? this._inputProvider : InputProvider.FILESYSTEM;
					} else {
						return this._inputProvider ? this._inputProvider : InputProvider.FILESYSTEM;
					}
				} else if ((this._inputProvider === InputProvider.SMART_CAPTURE || this._inputProvider === InputProvider.CAMERA) && isPredefinedJourney && selectedInputProvider !== InputProvider.NONE) {
					return selectedInputProvider;
				} else {
					return this._providers[0];
				}
			} else {
				return InputProvider.NONE;
			}
		});
	}

	private static toggleUploadLabel(this: JourneyContainer, toggle: boolean, uploadLabel: HTMLElement, uploadToggleButton: HTMLElement, cameraLabel: HTMLElement) {
		if (toggle) {
			RenderingService.hideObject(cameraLabel);
			if (this._config.fileUploadOnCameraCaptureJourneys == true) {
				RenderingService.showObject(uploadLabel);
				RenderingService.showObject(uploadToggleButton);
			}
			else {
				RenderingService.hideObject(uploadLabel);
				RenderingService.hideObject(uploadToggleButton);
			}
		} else {
			RenderingService.hideObject(uploadLabel);
			CameraService.getVideoCameras()
			Promise.all([
				CameraService.getCameraPermission(),
				CameraService.hasDeviceCapableOfResolution()
			])
				.then(([permission, resolution]) => {
					if (permission && resolution) { // Check has camera there is
						RenderingService.showObject(cameraLabel);
					}
					else { //There is no camera
						RenderingService.hideObject(cameraLabel);
					}
				})
		}
	}

}
